import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { ZoomIn } from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { Button } from '@/components/ui/Button';
import { useThemePalette } from '@/config/theme';

type IoniconName = keyof typeof Ionicons.glyphMap;

/**
 * Auto-shows a game's "how to play" pop-up ONCE (keyed by `gameKey` in
 * AsyncStorage) and exposes `open` to re-show it from a header info button.
 */
export function useHowToPlay(gameKey: string) {
  const [visible, setVisible] = useState(false);
  const decided = useRef(false);
  useEffect(() => {
    if (decided.current) return;
    decided.current = true;
    AsyncStorage.getItem(`tashkeela.howto.${gameKey}`)
      .then((seen) => {
        if (!seen) setVisible(true);
      })
      .catch(() => {});
  }, [gameKey]);
  const close = () => {
    setVisible(false);
    AsyncStorage.setItem(`tashkeela.howto.${gameKey}`, '1').catch(() => {});
  };
  return { visible, open: () => setVisible(true), close };
}

/** A small "?" header action that re-opens the how-to-play pop-up. */
export function HowToPlayButton({ onPress }: { onPress: () => void }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t('common.howToPlay')}
      onPress={onPress}
      hitSlop={8}
      className="h-9 w-9 items-center justify-center rounded-full bg-surface active:bg-surface-2"
    >
      <Ionicons name="help-circle-outline" size={20} color={palette.soft} />
    </Pressable>
  );
}

/**
 * The pop-up itself. Reads its title + step list from i18n at `howto.<i18nKey>`
 * (steps is a string[] via returnObjects), so adding a game is just i18n + an
 * icon. Tap outside or "Got it" to dismiss.
 */
export function HowToPlay({
  visible,
  onClose,
  i18nKey,
  icon,
}: {
  visible: boolean;
  onClose: () => void;
  i18nKey: string;
  icon: IoniconName;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const steps = (t(`howto.${i18nKey}.steps`, { returnObjects: true }) as unknown) as string[];
  const list = Array.isArray(steps) ? steps : [];

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable onPress={onClose} className="flex-1 items-center justify-center bg-black/60 px-8">
        <Animated.View entering={ZoomIn.springify().damping(16)} className="w-full">
          {/* Absorbs taps so the backdrop press doesn't close it. */}
          <Pressable onPress={() => {}} className="gap-4 rounded-card border border-line bg-surface px-5 py-6">
            <View className="items-center gap-2">
              <View className="h-12 w-12 items-center justify-center rounded-2xl bg-volt-glass">
                <Ionicons name={icon} size={24} color={palette.voltBright} />
              </View>
              <AppText variant="title" weight="bold" className="text-center text-ink">
                {t(`howto.${i18nKey}.title`)}
              </AppText>
            </View>
            <View className="gap-2.5">
              {list.map((s, i) => (
                <View key={i} className="flex-row gap-2.5">
                  <View className="mt-0.5 h-5 w-5 items-center justify-center rounded-full bg-volt/15">
                    <AppText variant="micro" weight="bold" tabular className="text-volt-bright">{i + 1}</AppText>
                  </View>
                  <AppText variant="caption" className="flex-1 leading-5 text-soft">{s}</AppText>
                </View>
              ))}
            </View>
            <Button label={t('common.gotIt')} onPress={onClose} />
          </Pressable>
        </Animated.View>
      </Pressable>
    </Modal>
  );
}
